import React from 'react';

import { usePermissions } from '../hooks/usePermissions';
import { useTranslation } from '../hooks/useTranslation';

/**
 * PermissionGuard component to protect routes based on permissions
 * @param {string} module - Required permission module (e.g., 'users', 'vehicles')
 * @param {string} action - Required permission action (e.g., 'view', 'create', 'edit', 'delete')
 * @param {array} permissions - Alternative: array of permission strings (e.g., ['users.view', 'admin.view'])
 * @param {boolean} requireAll - If true, user must have ALL permissions. If false, user needs ANY permission
 * @param {React.Component} children - Child components to render if permission check passes
 * @param {React.Component} fallback - Optional: Component to render if permission check fails
 */
export default function PermissionGuard({
  module,
  action,
  permissions,
  requireAll = false,
  children,
  fallback
}) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = usePermissions();
  const { getMessage } = useTranslation();

  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Check permissions
  let hasAccess = false;

  if (permissions && Array.isArray(permissions)) {
    // Check multiple permissions
    hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  } else if (module && action) {
    // Check single permission
    hasAccess = hasPermission(module, action);
  } else {
    console.error('PermissionGuard: Either (module + action) or (permissions array) must be provided');
    return null;
  }

  // If user has access, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // If custom fallback provided, render it
  if (fallback) {
    return fallback;
  }

  // Default: Show access denied message
  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md text-center">
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 dark:bg-red-900 rounded-full mb-4">
            <span className="text-5xl">🚫</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            {getMessage('errors.access_denied') || 'Access Denied'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-1">
            {getMessage('errors.no_permission') || 'You do not have permission to access this page.'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            {module && action && `Required permission: ${module}.${action}`}
            {permissions && `Required permissions: ${permissions.join(', ')}`}
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => window.history.back()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
          >
            ← {getMessage('common.go_back') || 'Go Back'}
          </button>

          <a
            href="/admin/dashboard"
            className="block w-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white py-2 px-4 rounded-lg transition-colors"
          >
            🏠 {getMessage('common.go_to_dashboard') || 'Go to Dashboard'}
          </a>
        </div>
      </div>
    </div>
  );
}
